/** Shared slot helpers for provider signup, settings, and availability grid. */

export const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

/** Allowed doctor availability slot lengths (minutes); each divides 60 for a clean hourly grid. */
export const DOCTOR_SLOT_INTERVAL_CHOICES = [10, 15, 20, 30, 60];

export const clampDoctorSlotIntervalMinutes = (value) => {
  const n = Math.round(Number(value));
  return DOCTOR_SLOT_INTERVAL_CHOICES.includes(n) ? n : 30;
};

export const minutesToHHMM = (totalMins) => {
  const m = Math.max(0, Math.min(24 * 60, Math.round(Number(totalMins))));
  const h = Math.floor(m / 60) % 24;
  const mm = m % 60;
  return `${String(h).padStart(2, "0")}:${String(mm).padStart(2, "0")}`;
};

/** All start times for one day on a fixed step (e.g. 15 → 00:00 … 23:45). */
export const buildTimeOptionsForInterval = (stepMinutes) => {
  const step = clampDoctorSlotIntervalMinutes(stepMinutes);
  const out = [];
  for (let m = 0; m < 24 * 60; m += step) {
    out.push(minutesToHHMM(m));
  }
  return out;
};

export const TIME_OPTIONS_30_MIN = buildTimeOptionsForInterval(30);

export const timeToMinutes = (timeValue) => {
  const [hourText, minuteText] = String(timeValue || "").split(":");
  const hour = Number(hourText);
  const minute = Number(minuteText);
  if (!Number.isInteger(hour) || !Number.isInteger(minute)) return NaN;
  return hour * 60 + minute;
};

export const normalizeToSlotInterval = (timeValue, stepMinutes = 30) => {
  const step = clampDoctorSlotIntervalMinutes(stepMinutes);
  const mins = timeToMinutes(timeValue);
  if (!Number.isFinite(mins)) return minutesToHHMM(9 * 60);
  const maxStart = 24 * 60 - step;
  const normalized = Math.max(0, Math.min(maxStart, Math.round(mins / step) * step));
  return minutesToHHMM(normalized);
};

export const normalizeTo30Minute = (timeValue) => normalizeToSlotInterval(timeValue, 30);

export const isValid30MinuteSlot = (row) => {
  const startMinutes = timeToMinutes(row?.startTime);
  const endMinutes = timeToMinutes(row?.endTime);
  if (!Number.isFinite(startMinutes) || !Number.isFinite(endMinutes)) return false;
  if (startMinutes % 30 !== 0 || endMinutes % 30 !== 0) return false;
  return endMinutes - startMinutes >= 30;
};

export const formatDisplayTime = (timeValue) => {
  const [hourText, minuteText] = String(timeValue || "00:00").split(":");
  const hour = Number(hourText);
  const minute = Number(minuteText);
  const safeHour = Number.isFinite(hour) ? hour : 0;
  const safeMinute = Number.isFinite(minute) ? minute : 0;
  const suffix = safeHour >= 12 ? "PM" : "AM";
  const clockHour = safeHour % 12 || 12;
  return `${clockHour}:${String(safeMinute).padStart(2, "0")} ${suffix}`;
};

function parseCellKey(key) {
  const i = String(key || "").indexOf("|");
  if (i < 0) return { day: "", minutes: NaN };
  return { day: key.slice(0, i), minutes: timeToMinutes(key.slice(i + 1)) };
}

function intervalFullySelected(day, tStart, tEnd, cellSet, oldStep) {
  for (let m = tStart; m < tEnd; m += oldStep) {
    if (!cellSet.has(`${day}|${minutesToHHMM(m)}`)) return false;
  }
  return true;
}

/**
 * When the doctor changes slot length, remap per-clinic cell sets between grids.
 * Each cell key is `${day}|${HH:MM}` marking one interval of length `oldStep` / `newStep`.
 */
export const migrateClinicCellsMap = (cellsMap, oldStep, newStep) => {
  const a = clampDoctorSlotIntervalMinutes(oldStep);
  const b = clampDoctorSlotIntervalMinutes(newStep);
  if (a === b) return cellsMap;
  const next = new Map();
  for (const [id, clinic] of cellsMap.entries()) {
    const oldCells = clinic.cells;
    const newSet = new Set();
    if (b < a) {
      for (const key of oldCells) {
        const { day, minutes } = parseCellKey(key);
        if (!DAYS.includes(day) || !Number.isFinite(minutes)) continue;
        for (let t = minutes; t < minutes + a; t += b) {
          newSet.add(`${day}|${minutesToHHMM(t)}`);
        }
      }
    } else {
      const byDay = new Map();
      for (const key of oldCells) {
        const { day, minutes } = parseCellKey(key);
        if (!DAYS.includes(day) || !Number.isFinite(minutes)) continue;
        if (!byDay.has(day)) byDay.set(day, []);
        byDay.get(day).push(minutes);
      }
      for (const [day, starts] of byDay) {
        const uniq = [...new Set(starts)].sort((x, y) => x - y);
        if (!uniq.length) continue;
        const minM = uniq[0];
        const maxM = uniq[uniq.length - 1] + a;
        for (let t = Math.floor(minM / b) * b; t < maxM + b; t += b) {
          if (t + b > 24 * 60) break;
          if (intervalFullySelected(day, t, t + b, oldCells, a)) {
            newSet.add(`${day}|${minutesToHHMM(t)}`);
          }
        }
      }
    }
    next.set(id, { ...clinic, cells: newSet });
  }
  return next;
};

/** Remap one clinic's cells when its slot length changes; updates that entry's `slotIntervalMinutes`. */
export const migrateSingleClinicCells = (cellsMap, clinicId, oldStep, newStep) => {
  const a = clampDoctorSlotIntervalMinutes(oldStep);
  const b = clampDoctorSlotIntervalMinutes(newStep);
  if (a === b) return cellsMap;
  const prev = cellsMap.get(clinicId);
  if (!prev) return cellsMap;
  const sub = new Map([[clinicId, { ...prev, cells: new Set(prev.cells) }]]);
  const migrated = migrateClinicCellsMap(sub, a, b);
  const updated = migrated.get(clinicId);
  if (!updated) return cellsMap;
  const next = new Map(cellsMap);
  next.set(clinicId, { ...prev, cells: updated.cells, slotIntervalMinutes: b });
  return next;
};

export const newDoctorSlotRow = () => ({
  id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
  day: "Mon",
  startTime: "09:00",
  endTime: "13:00",
  location: "",
});

export const newHospitalScheduleRow = () => ({
  id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
  departmentName: "General OPD",
  day: "Mon",
  startTime: "09:00",
  endTime: "17:00",
  slotDurationMinutes: 30,
  notes: "",
});

export const slotBlocksToDoctorRows = (blocks, fallbackStepMinutes = 30) => {
  if (!Array.isArray(blocks)) return [];
  return blocks.map((b, i) => {
    const step = clampDoctorSlotIntervalMinutes(
      b.slotStepMinutes ?? b.slotIntervalMinutes ?? fallbackStepMinutes
    );
    return {
    id: `loaded-${i}-${b.day}-${b.startTime}`,
    day: DAYS.includes(b.day) ? b.day : "Mon",
    startTime: normalizeToSlotInterval(b.startTime, step),
    endTime: normalizeToSlotInterval(b.endTime, step),
    location: String(b.location || "").trim(),
    clinicId: String(b.clinicId || "").trim(),
    slotStepMinutes: step,
    address: String(b.address || "").trim(),
    city: String(b.city || "").trim(),
    state: String(b.state || "").trim(),
    pincode: String(b.pincode || "").trim(),
    latitude: Number.isFinite(Number(b.latitude)) ? Number(b.latitude) : null,
    longitude: Number.isFinite(Number(b.longitude)) ? Number(b.longitude) : null,
    googlePlaceId: String(b.googlePlaceId || "").trim(),
    phoneNumber: String(b.phoneNumber || "").trim(),
    placePhotoUrl: String(b.placePhotoUrl || "").trim(),
    placePhotoUrls: Array.isArray(b.placePhotoUrls)
      ? b.placePhotoUrls.map((x) => String(x || "").trim()).filter(Boolean).slice(0, 8)
      : b.placePhotoUrl
        ? [String(b.placePhotoUrl).trim()]
        : [],
  };
  });
};

export const schedulesToHospitalRows = (schedules) => {
  if (!Array.isArray(schedules)) return [];
  return schedules.flatMap((s, i) => {
    const days = Array.isArray(s.weekdays) && s.weekdays.length ? s.weekdays : ["Mon"];
    return days.map((day, j) => ({
      id: `h-${i}-${j}-${day}`,
      departmentName: String(s.departmentName || "General OPD").trim() || "General OPD",
      day: DAYS.includes(day) ? day : "Mon",
      startTime: normalizeTo30Minute(s.startTime),
      endTime: normalizeTo30Minute(s.endTime),
      slotDurationMinutes: Number(s.slotDurationMinutes) || 30,
      notes: String(s.notes || "").trim(),
    }));
  });
};

export const isValidHospitalScheduleRow = (row) => {
  if (!row || String(row.departmentName || "").trim().length < 2) return false;
  if (!DAYS.includes(row.day)) return false;
  const sm = timeToMinutes(row.startTime);
  const em = timeToMinutes(row.endTime);
  if (!Number.isFinite(sm) || !Number.isFinite(em) || em <= sm) return false;
  const d = Number(row.slotDurationMinutes);
  if (!Number.isFinite(d) || d < 5 || d > 120) return false;
  return true;
};
